import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { PlusIcon, EyeIcon, ChevronUpIcon, ChevronDownIcon, MagnifyingGlassIcon, CubeIcon, XMarkIcon } from '@heroicons/react/20/solid'
import { WrenchScrewdriverIcon } from '@heroicons/react/24/outline'
import { serviceCasesApi, assetsApi, type ServiceCaseWithRelations } from '../../lib/api'
import { ServiceCaseForm } from '../../components/forms/ServiceCaseForm'
import { ServiceCaseStatusBadge } from '../../components/ui/ServiceCaseStatusBadge'
import { ServiceCasePriorityBadge } from '../../components/ui/ServiceCasePriorityBadge'
import { ServiceCaseCategoryBadge } from '../../components/ui/ServiceCaseCategoryBadge'
import { Pagination } from '../../components/ui/Pagination'
import type { ServiceCaseInput, ServiceCaseStatus, ServiceCasePriority, ServiceCaseCategory } from '../../../shared/types'

const STATUSES = ['open', 'in_progress', 'waiting_customer', 'waiting_parts', 'resolved', 'closed'] as const
const PRIORITIES = ['low', 'medium', 'high', 'critical'] as const
const CATEGORIES = ['part_request', 'error_code', 'maintenance', 'documentation', 'other'] as const
type SortField = 'caseNumber' | 'subject' | 'status' | 'priority' | 'assignedTo' | 'createdAt'
type SortDirection = 'asc' | 'desc'

const STATUS_ORDER: Record<ServiceCaseStatus, number> = {
  open: 0,
  in_progress: 1,
  waiting_customer: 2,
  waiting_parts: 3,
  resolved: 4,
  closed: 5,
}

const PRIORITY_ORDER: Record<ServiceCasePriority, number> = {
  critical: 0,
  high: 1,
  medium: 2,
  low: 3,
}

export function ServiceCasesListPage() {
  const [showForm, setShowForm] = useState(false)
  const [statusFilter, setStatusFilter] = useState<ServiceCaseStatus | 'all'>('all')
  const [priorityFilter, setPriorityFilter] = useState<ServiceCasePriority | 'all'>('all')
  const [categoryFilter, setCategoryFilter] = useState<ServiceCaseCategory | 'all'>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [assetSearch, setAssetSearch] = useState('')
  const [selectedAssetId, setSelectedAssetId] = useState<string | null>(null)
  const [sortField, setSortField] = useState<SortField>('createdAt')
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(20)
  const queryClient = useQueryClient()

  // Search assets by serial number for quick filter
  const { data: assetSearchResults } = useQuery({
    queryKey: ['assets', 'search', assetSearch],
    queryFn: () => assetsApi.list({ limit: 5, filters: { search: assetSearch } }),
    enabled: assetSearch.length >= 2,
  })

  // Build filters object for API
  const filters: Record<string, string> = {}
  if (statusFilter !== 'all') filters.status = statusFilter
  if (priorityFilter !== 'all') filters.priority = priorityFilter
  if (categoryFilter !== 'all') filters.category = categoryFilter
  if (selectedAssetId) filters.assetId = selectedAssetId

  const { data: response, isLoading } = useQuery({
    queryKey: ['service-cases', { page, limit, filters }],
    queryFn: () => serviceCasesApi.list({ page, limit, filters: Object.keys(filters).length > 0 ? filters : undefined }),
  })

  const { data: stats } = useQuery({
    queryKey: ['service-cases', 'stats'],
    queryFn: () => serviceCasesApi.getStats(),
  })

  const cases = response?.data

  // Handle filter changes - reset to page 1
  const handleStatusFilter = (status: ServiceCaseStatus | 'all') => {
    setStatusFilter(status)
    setPage(1)
  }

  const handlePriorityFilter = (priority: ServiceCasePriority | 'all') => {
    setPriorityFilter(priority)
    setPage(1)
  }

  const handleCategoryFilter = (category: ServiceCaseCategory | 'all') => {
    setCategoryFilter(category)
    setPage(1)
  }

  const handleAssetSelect = (assetId: string, serialNumber: string) => {
    setSelectedAssetId(assetId)
    setAssetSearch(serialNumber)
    setPage(1)
  }

  const clearAssetFilter = () => {
    setSelectedAssetId(null)
    setAssetSearch('')
    setPage(1)
  }

  const createMutation = useMutation({
    mutationFn: serviceCasesApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['service-cases'] })
      setShowForm(false)
    },
  })

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection(field === 'createdAt' ? 'desc' : 'asc')
    }
  }

  // Client-side sorting and text search
  const sortedCases = useMemo(() => {
    if (!cases) return []

    let result = [...cases]

    // Apply text search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      result = result.filter(
        (c) =>
          c.caseNumber.toLowerCase().includes(query) ||
          c.subject.toLowerCase().includes(query) ||
          c.description?.toLowerCase().includes(query) ||
          c.asset?.serialNumber?.toLowerCase().includes(query)
      )
    }

    result.sort((a, b) => {
      let comparison = 0

      switch (sortField) {
        case 'caseNumber':
          comparison = a.caseNumber.localeCompare(b.caseNumber)
          break
        case 'subject':
          comparison = a.subject.localeCompare(b.subject)
          break
        case 'status':
          comparison = STATUS_ORDER[a.status] - STATUS_ORDER[b.status]
          break
        case 'priority':
          comparison = PRIORITY_ORDER[a.priority] - PRIORITY_ORDER[b.priority]
          break
        case 'assignedTo':
          const nameA = a.assignedTo ? `${a.assignedTo.firstName} ${a.assignedTo.lastName}`.toLowerCase() : 'zzz'
          const nameB = b.assignedTo ? `${b.assignedTo.firstName} ${b.assignedTo.lastName}`.toLowerCase() : 'zzz'
          comparison = nameA.localeCompare(nameB)
          break
        case 'createdAt':
          comparison = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
          break
      }

      return sortDirection === 'asc' ? comparison : -comparison
    })

    return result
  }, [cases, sortField, sortDirection, searchQuery])

  const SortableHeader = ({ field, children }: { field: SortField; children: React.ReactNode }) => {
    const isActive = sortField === field
    return (
      <th
        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 cursor-pointer hover:bg-gray-100 select-none"
        onClick={() => handleSort(field)}
      >
        <div className="flex items-center gap-1">
          {children}
          <div className="flex flex-col -space-y-1">
            <ChevronUpIcon
              className={`h-3 w-3 ${isActive && sortDirection === 'asc' ? 'text-gray-900' : 'text-gray-300'}`}
            />
            <ChevronDownIcon
              className={`h-3 w-3 ${isActive && sortDirection === 'desc' ? 'text-gray-900' : 'text-gray-300'}`}
            />
          </div>
        </div>
      </th>
    )
  }

  if (isLoading) return <div className="p-8">Loading...</div>

  return (
    <>
      {/* Secondary column - Actions and filters */}
      <aside className="fixed inset-y-4 left-72 hidden w-[calc((100vw-18rem-2rem)/3)] overflow-y-auto bg-white px-4 py-6 sm:px-6 lg:px-8 xl:block rounded-xl shadow-[4px_4px_12px_rgba(0,0,0,0.08)]">
        <div className="space-y-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Service Cases</h2>
            <p className="mt-1 text-sm text-gray-500">
              Manage service requests and support cases
            </p>
          </div>

          <Link
            to="/service-cases/new"
            className="w-full flex items-center justify-center gap-x-2 rounded-md px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:opacity-90 bg-brand-500"
          >
            <PlusIcon className="h-5 w-5" />
            New Case
          </Link>

          {/* Search */}
          <div className="bg-white p-4 rounded-lg shadow-sm ring-1 ring-gray-900/5">
            <h3 className="text-sm font-medium text-gray-900 mb-3">Search</h3>
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Case #, subject, description..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
              />
            </div>
          </div>

          {/* Asset Filter */}
          <div className="bg-white p-4 rounded-lg shadow-sm ring-1 ring-gray-900/5">
            <h3 className="text-sm font-medium text-gray-900 mb-3">Filter by Asset</h3>
            {selectedAssetId ? (
              <div className="flex items-center justify-between bg-brand-50 rounded-lg px-3 py-2">
                <div className="flex items-center gap-2">
                  <CubeIcon className="h-4 w-4 text-brand-600" />
                  <span className="text-sm font-medium text-brand-700">{assetSearch}</span>
                </div>
                <button
                  onClick={clearAssetFilter}
                  className="text-brand-600 hover:text-brand-800"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <div className="relative">
                <CubeIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by serial number..."
                  value={assetSearch}
                  onChange={(e) => setAssetSearch(e.target.value)}
                  className="pl-9 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
                />
                {assetSearchResults && assetSearchResults.data.length > 0 && assetSearch.length >= 2 && !selectedAssetId && (
                  <div className="absolute z-10 mt-1 w-full bg-white rounded-md shadow-lg border border-gray-200 max-h-48 overflow-auto">
                    {assetSearchResults.data.map((asset) => (
                      <button
                        key={asset.id}
                        onClick={() => handleAssetSelect(asset.id, asset.serialNumber)}
                        className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center gap-2"
                      >
                        <CubeIcon className="h-4 w-4 text-gray-400" />
                        <div>
                          <span className="text-sm font-medium text-gray-900">{asset.serialNumber}</span>
                          {asset.account && (
                            <span className="text-xs text-gray-500 ml-2">{asset.account.name}</span>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Status Filter */}
          <div className="bg-white p-4 rounded-lg shadow-sm ring-1 ring-gray-900/5">
            <h3 className="text-sm font-medium text-gray-900 mb-3">Status</h3>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => handleStatusFilter('all')}
                className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                  statusFilter === 'all'
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                All
              </button>
              {STATUSES.map(status => (
                <button
                  key={status}
                  onClick={() => handleStatusFilter(status)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                    statusFilter === status
                      ? 'bg-gray-900 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {status.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                </button>
              ))}
            </div>
          </div>

          {/* Priority Filter */}
          <div className="bg-white p-4 rounded-lg shadow-sm ring-1 ring-gray-900/5">
            <h3 className="text-sm font-medium text-gray-900 mb-3">Priority</h3>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => handlePriorityFilter('all')}
                className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                  priorityFilter === 'all'
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                All
              </button>
              {PRIORITIES.map(priority => (
                <button
                  key={priority}
                  onClick={() => handlePriorityFilter(priority)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                    priorityFilter === priority
                      ? 'bg-gray-900 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {priority.charAt(0).toUpperCase() + priority.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {/* Category Filter */}
          <div className="bg-white p-4 rounded-lg shadow-sm ring-1 ring-gray-900/5">
            <h3 className="text-sm font-medium text-gray-900 mb-3">Category</h3>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => handleCategoryFilter('all')}
                className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                  categoryFilter === 'all'
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                All
              </button>
              {CATEGORIES.map(category => (
                <button
                  key={category}
                  onClick={() => handleCategoryFilter(category)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                    categoryFilter === category
                      ? 'bg-gray-900 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {category.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                </button>
              ))}
            </div>
          </div>

          {/* Stats */}
          <div className="bg-white p-4 rounded-lg shadow-sm ring-1 ring-gray-900/5">
            <h3 className="text-sm font-medium text-gray-900 mb-3">Quick Stats</h3>
            <dl className="space-y-2">
              <div className="flex justify-between text-sm">
                <dt className="text-gray-500">Open</dt>
                <dd className="font-medium text-gray-900">{stats?.open || 0}</dd>
              </div>
              <div className="flex justify-between text-sm">
                <dt className="text-gray-500">In Progress</dt>
                <dd className="font-medium text-gray-900">{stats?.in_progress || 0}</dd>
              </div>
              <div className="flex justify-between text-sm">
                <dt className="text-gray-500">Waiting</dt>
                <dd className="font-medium text-gray-900">{(stats?.waiting_customer || 0) + (stats?.waiting_parts || 0)}</dd>
              </div>
              <div className="flex justify-between text-sm">
                <dt className="text-gray-500">Unassigned</dt>
                <dd className="font-medium text-gray-900">{stats?.unassigned || 0}</dd>
              </div>
              <div className="flex justify-between text-sm border-t pt-2">
                <dt className="text-gray-500">Total</dt>
                <dd className="font-medium text-gray-900">{stats?.total || 0}</dd>
              </div>
            </dl>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="xl:pl-[calc((100vw-18rem-2rem)/3)] xl:ml-4 p-4 xl:p-0 xl:pr-4 xl:py-4">
        <div className="bg-white rounded-xl shadow-[4px_4px_12px_rgba(0,0,0,0.08)] min-h-[calc(100vh-2rem)] px-4 py-10 sm:px-6 lg:px-8 lg:py-6 overflow-hidden">
          {/* Mobile header */}
          <div className="xl:hidden mb-6">
            <h1 className="text-2xl font-semibold text-gray-900">Service Cases</h1>
            <Link
              to="/service-cases/new"
              className="mt-4 w-full flex items-center justify-center gap-x-2 rounded-md px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:opacity-90 bg-brand-500"
            >
              <PlusIcon className="h-5 w-5" />
              New Case
            </Link>

            {/* Mobile search */}
            <div className="mt-4 relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search cases..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
              />
            </div>
          </div>

          {/* Active Filters Display */}
          {(statusFilter !== 'all' || priorityFilter !== 'all' || categoryFilter !== 'all' || selectedAssetId || searchQuery) && (
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <span className="text-sm text-gray-500">Active filters:</span>
              {searchQuery && (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                  Search: "{searchQuery}"
                  <button onClick={() => setSearchQuery('')} className="ml-1 hover:text-gray-900">
                    <XMarkIcon className="h-3 w-3" />
                  </button>
                </span>
              )}
              {selectedAssetId && (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-brand-100 text-brand-700">
                  Asset: {assetSearch}
                  <button onClick={clearAssetFilter} className="ml-1 hover:text-brand-900">
                    <XMarkIcon className="h-3 w-3" />
                  </button>
                </span>
              )}
              {statusFilter !== 'all' && (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                  Status: {statusFilter.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                  <button onClick={() => handleStatusFilter('all')} className="ml-1 hover:text-blue-900">
                    <XMarkIcon className="h-3 w-3" />
                  </button>
                </span>
              )}
              {priorityFilter !== 'all' && (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
                  Priority: {priorityFilter.charAt(0).toUpperCase() + priorityFilter.slice(1)}
                  <button onClick={() => handlePriorityFilter('all')} className="ml-1 hover:text-orange-900">
                    <XMarkIcon className="h-3 w-3" />
                  </button>
                </span>
              )}
              {categoryFilter !== 'all' && (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                  Category: {categoryFilter.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                  <button onClick={() => handleCategoryFilter('all')} className="ml-1 hover:text-purple-900">
                    <XMarkIcon className="h-3 w-3" />
                  </button>
                </span>
              )}
              <button
                onClick={() => {
                  setSearchQuery('')
                  clearAssetFilter()
                  handleStatusFilter('all')
                  handlePriorityFilter('all')
                  handleCategoryFilter('all')
                }}
                className="text-xs text-gray-500 hover:text-gray-700 underline"
              >
                Clear all
              </button>
            </div>
          )}

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <SortableHeader field="caseNumber">Case #</SortableHeader>
                  <SortableHeader field="subject">Subject</SortableHeader>
                  <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Asset</th>
                  <SortableHeader field="status">Status</SortableHeader>
                  <SortableHeader field="priority">Priority</SortableHeader>
                  <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Category</th>
                  <SortableHeader field="assignedTo">Assigned To</SortableHeader>
                  <SortableHeader field="createdAt">Created</SortableHeader>
                  <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {sortedCases.map((serviceCase) => (
                  <tr key={serviceCase.id} className="hover:bg-gray-50">
                    <td className="whitespace-nowrap px-3 py-4 text-sm font-medium text-gray-900">
                      <Link to={`/service-cases/${serviceCase.id}`} className="hover:text-brand-600 flex items-center gap-2">
                        <WrenchScrewdriverIcon className="h-4 w-4 text-gray-400" />
                        {serviceCase.caseNumber}
                      </Link>
                    </td>
                    <td className="px-3 py-4 text-sm text-gray-900">
                      <div className="max-w-xs truncate">
                        <Link to={`/service-cases/${serviceCase.id}`} className="hover:text-brand-600">
                          {serviceCase.subject}
                        </Link>
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      {serviceCase.asset ? (
                        <Link to={`/assets/${serviceCase.assetId}`} className="hover:text-brand-600 flex items-center gap-1">
                          <CubeIcon className="h-3 w-3 text-gray-400" />
                          {serviceCase.asset.serialNumber}
                        </Link>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm">
                      <ServiceCaseStatusBadge status={serviceCase.status} />
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm">
                      <ServiceCasePriorityBadge priority={serviceCase.priority} />
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm">
                      <ServiceCaseCategoryBadge category={serviceCase.category} />
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      {serviceCase.assignedTo ? (
                        `${serviceCase.assignedTo.firstName} ${serviceCase.assignedTo.lastName}`
                      ) : (
                        <span className="text-gray-400">Unassigned</span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      {new Date(serviceCase.createdAt).toLocaleDateString()}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm">
                      <Link
                        to={`/service-cases/${serviceCase.id}`}
                        className="text-brand-600 hover:text-brand-900"
                      >
                        <EyeIcon className="h-5 w-5" />
                      </Link>
                    </td>
                  </tr>
                ))}
                {sortedCases.length === 0 && (
                  <tr>
                    <td colSpan={9} className="px-3 py-8 text-center text-sm text-gray-500">
                      {searchQuery || selectedAssetId || statusFilter !== 'all' || priorityFilter !== 'all' || categoryFilter !== 'all'
                        ? 'No service cases match your filters'
                        : 'No service cases found'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {response && response.totalPages > 1 && (
            <div className="mt-6">
              <Pagination
                page={page}
                totalPages={response.totalPages}
                total={response.total}
                limit={limit}
                onPageChange={setPage}
                onLimitChange={(newLimit) => {
                  setLimit(newLimit)
                  setPage(1)
                }}
              />
            </div>
          )}
        </div>
      </div>
    </>
  )
}
